import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Logo } from '../components/Logo';
import { GoogleLoginButton } from '../components/GoogleLoginButton';

export function AuthPage({ mode: initialMode = 'login' }) {
  const { login, register, loginGoogle, inviteInfo, redeemInvite, error: authError } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Lê ?mode=register da URL (deep link direto para a aba de cadastro)
  const [mode, setMode] = useState(() => {
    const fromUrl = searchParams.get('mode');
    if (fromUrl === 'register' || fromUrl === 'login') return fromUrl;
    return initialMode;
  });
  // LE-040: cadastro por convite tem dois passos — 'code' (código + e-mail) e
  // 'confirm' (confirmar dados + criar senha). O e-mail fica travado após a
  // validação (o código é vinculado ao e-mail do convite).
  const [inviteStep, setInviteStep] = useState('code');
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '', code: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const set = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      if (mode === 'login') {
        await login(form.email, form.password);
      } else if (inviteStep === 'code') {
        // Passo 1: valida o código + e-mail e recebe os dados a confirmar
        const data = await inviteInfo({ code: form.code, email: form.email });
        setForm(prev => ({
          ...prev,
          name: data.name || prev.name,
          phone: data.phone || prev.phone,
        }));
        setInviteStep('confirm');
      } else {
        // Passo 2: confirma os dados e cria a senha (primeiro acesso)
        await redeemInvite({
          code: form.code,
          email: form.email,
          name: form.name,
          phone: form.phone || undefined,
          password: form.password,
        });
        navigate('/perfil');
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Erro ao processar. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (next) => {
    setMode(next);
    setInviteStep('code');
    setError(null);
    navigate(next === 'login' ? '/entrar' : '/entrar?mode=register', { replace: true });
  };

  const goBackToCode = () => {
    setInviteStep('code');
    setError(null);
  };

  const inputStyle = {
    width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid var(--nc-border)',
    fontSize: 14, marginBottom: 10, outline: 'none', background: 'var(--nc-surface)', color: 'var(--nc-text)',
    boxSizing: 'border-box', transition: 'border-color 0.15s ease'
  };

  const isRegister = mode === 'register';
  const isConfirmStep = isRegister && inviteStep === 'confirm';
  const passwordOk = form.password.length >= 8;
  const passwordsMatch = form.password === form.confirmPassword;

  return (
    <div className="nc-page nc-page--col">
      <div style={{
        background: 'linear-gradient(135deg, var(--nc-primary) 0%, var(--nc-primary-dark) 100%)',
        color: 'var(--nc-on-primary)', padding: '32px 20px 40px', borderRadius: '0 0 28px 28px'
      }}>
        <Logo size={40} showText={false} />
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: '18px 0 6px' }}>
          {mode === 'login' ? 'Bem-vindo de volta!' : 'Crie sua conta'}
        </h1>
        <p style={{ fontSize: 13, opacity: 0.85, margin: 0, lineHeight: 1.5 }}>
          {mode === 'login'
            ? 'Entre para favoritar, avaliar e publicar na sua cidade.'
            : 'Use o código de acesso que você recebeu.'}
        </p>
      </div>

      <div style={{ padding: 20, flex: 1, marginTop: -14 }}>
        <div style={{
          background: 'var(--nc-surface)', borderRadius: 16, border: '1px solid var(--nc-border)',
          boxShadow: 'var(--nc-shadow-lg)', padding: 20, maxWidth: 420, margin: '0 auto'
        }}>
          {/* LE-034: login com Google — renderiza apenas se o build tiver
              REACT_APP_GOOGLE_CLIENT_ID (desabilitado → botão não aparece) */}
          <GoogleLoginButton
            onSuccess={async (credential) => {
              await loginGoogle(credential);
              navigate('/perfil');
            }}
            onError={(err) => setError(err.response?.data?.error?.message || err.message || 'Erro ao entrar com Google. Tente novamente.')}
          />

          {/* Tabs */}
          <div style={{ display: 'flex', background: 'var(--nc-surface-alt)', borderRadius: 10, padding: 4, marginBottom: 18 }}>
            {(['login', 'register']).map(m => (
              <button
                key={m}
                type="button"
                onClick={() => switchMode(m)}
                aria-pressed={mode === m}
                style={{
                  flex: 1, padding: '9px 0', borderRadius: 8, border: 'none', cursor: 'pointer',
                  fontSize: 13, fontWeight: 600,
                  background: mode === m ? '#fff' : 'transparent',
                  color: mode === m ? '#0d5c3b' : '#6b7280',
                  boxShadow: mode === m ? 'var(--nc-shadow)' : 'none',
                  transition: 'all 0.15s ease'
                }}
              >
                {m === 'login' ? 'Entrar' : 'Criar conta'}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} noValidate>
            {isRegister && !isConfirmStep && (
              <>
                <input
                  type="text"
                  inputMode="text"
                  placeholder="Código de acesso (ex.: A7K3-M9XQ)"
                  value={form.code}
                  onChange={set('code')}
                  required
                  aria-label="Código de acesso"
                  style={{ ...inputStyle, textTransform: 'uppercase', letterSpacing: 1, fontFamily: 'monospace' }}
                />
                <input
                  type="email"
                  placeholder="Seu e-mail (o mesmo do convite)"
                  value={form.email}
                  onChange={set('email')}
                  required
                  aria-label="E-mail"
                  style={inputStyle}
                />
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    width: '100%', padding: '13px 0', borderRadius: 10, border: 'none', cursor: 'pointer',
                    background: loading ? 'rgba(13,92,59,0.7)' : '#0d5c3b', color: 'var(--nc-on-primary)',
                    fontSize: 14, fontWeight: 600, marginTop: 4,
                    transition: 'all 0.15s ease', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
                  }}
                >
                  {loading && <span className="nc-pulse" style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.4)', borderTopColor: 'var(--nc-surface)' }} />}
                  {loading ? 'Validando...' : 'Continuar'}
                </button>
                {(error || authError) && (
                  <div role="alert" style={{
                    fontSize: 12, color: 'var(--nc-danger-text)', background: 'var(--nc-danger-soft)',
                    border: '1px solid var(--nc-danger-border)', borderRadius: 8, padding: '10px 12px', marginBottom: 10
                  }}>
                    {error || authError}
                  </div>
                )}
                <p className="nc-hint" style={{ margin: '12px 0 0', textAlign: 'center', lineHeight: 1.5 }}>
                  O código é enviado pelo administrador (ex.: pelo WhatsApp) e vale por 24 horas.
                </p>
              </>
            )}

            {isConfirmStep && (
              <>
                <input
                  type="email"
                  value={form.email}
                  readOnly
                  aria-label="E-mail (do convite)"
                  style={{ ...inputStyle, background: 'var(--nc-surface-alt)', color: 'var(--nc-text-secondary)' }}
                />
                <input
                  type="text"
                  placeholder="Seu nome completo"
                  value={form.name}
                  onChange={set('name')}
                  required
                  minLength={2}
                  aria-label="Nome completo"
                  style={inputStyle}
                />
                <input
                  type="tel"
                  placeholder="WhatsApp (opcional)"
                  value={form.phone}
                  onChange={set('phone')}
                  aria-label="WhatsApp"
                  style={inputStyle}
                />
                <input
                  type="password"
                  placeholder="Crie uma senha (mínimo 8 caracteres)"
                  value={form.password}
                  onChange={set('password')}
                  required
                  minLength={8}
                  aria-label="Senha"
                  style={{ ...inputStyle, borderColor: form.password && !passwordOk ? '#fca5a5' : undefined }}
                />
                <input
                  type="password"
                  placeholder="Confirme a senha"
                  value={form.confirmPassword}
                  onChange={set('confirmPassword')}
                  required
                  minLength={8}
                  aria-label="Confirme a senha"
                  style={{ ...inputStyle, borderColor: form.confirmPassword && !passwordsMatch ? '#fca5a5' : undefined }}
                />
                {form.password && !passwordOk && (
                  <p style={{ fontSize: 12, color: 'var(--nc-danger-text)', margin: '-4px 0 10px' }}>A senha deve ter pelo menos 8 caracteres.</p>
                )}
                {form.confirmPassword && !passwordsMatch && (
                  <p style={{ fontSize: 12, color: 'var(--nc-danger-text)', margin: '-4px 0 10px' }}>As senhas não conferem.</p>
                )}

                {(error || authError) && (
                  <div role="alert" style={{
                    fontSize: 12, color: 'var(--nc-danger-text)', background: 'var(--nc-danger-soft)',
                    border: '1px solid var(--nc-danger-border)', borderRadius: 8, padding: '10px 12px', marginBottom: 10
                  }}>
                    {error || authError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !passwordOk || !passwordsMatch}
                  style={{
                    width: '100%', padding: '13px 0', borderRadius: 10, border: 'none', cursor: 'pointer',
                    background: loading ? 'rgba(13,92,59,0.7)' : '#0d5c3b', color: 'var(--nc-on-primary)',
                    fontSize: 14, fontWeight: 600, marginTop: 4,
                    transition: 'all 0.15s ease', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
                  }}
                >
                  {loading && <span className="nc-pulse" style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.4)', borderTopColor: 'var(--nc-surface)' }} />}
                  {loading ? 'Criando conta...' : 'Criar conta e entrar'}
                </button>
                <button
                  type="button"
                  onClick={goBackToCode}
                 className="nc-body-sm" style={{ marginTop: 10, background: 'none', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'center' }}>
                  ← Voltar ao código
                </button>
              </>
            )}

            {mode === 'login' && (
              <>
                <input
                  type="email"
                  placeholder="Seu e-mail"
                  value={form.email}
                  onChange={set('email')}
                  required
                  aria-label="E-mail"
                  style={inputStyle}
                />
                <input
                  type="password"
                  placeholder="Sua senha"
                  value={form.password}
                  onChange={set('password')}
                  required
                  minLength={1}
                  aria-label="Senha"
                  style={inputStyle}
                />
                <p className="nc-hint" style={{ margin: '0 0 10px', lineHeight: 1.5 }}>
                  Ainda não tem acesso? Peça um <strong>código de convite</strong> à administração da sua cidade.
                </p>

                {(error || authError) && (
                  <div role="alert" style={{
                    fontSize: 12, color: 'var(--nc-danger-text)', background: 'var(--nc-danger-soft)',
                    border: '1px solid var(--nc-danger-border)', borderRadius: 8, padding: '10px 12px', marginBottom: 10
                  }}>
                    {error || authError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    width: '100%', padding: '13px 0', borderRadius: 10, border: 'none', cursor: 'pointer',
                    background: loading ? 'rgba(13,92,59,0.7)' : '#0d5c3b', color: 'var(--nc-on-primary)',
                    fontSize: 14, fontWeight: 600, marginTop: 4,
                    transition: 'all 0.15s ease', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
                  }}
                >
                  {loading && <span className="nc-pulse" style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.4)', borderTopColor: 'var(--nc-surface)' }} />}
                  {loading ? 'Entrando...' : 'Entrar'}
                </button>
              </>
            )}
          </form>

          <button
            onClick={() => navigate(-1)}
           className="nc-body-sm" style={{ marginTop: 14, background: 'none', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'center' }}>
            Voltar
          </button>
        </div>
      </div>
    </div>
  );
}
